import { useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "antd-mobile";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError, ToastSuccess } from "../../components/Toast";
import { copyTextToClipboard, staticUrl } from "../../utils";
import Avatar from "../../components/Avatar";

const ecodeBg = require("../../assets/images/user/ecode.jpg");
const logoImg = require("../../assets/images/user/logo.png");

const QRCODE_ID = "RECEIVE_QRCODE";

const Body = styled.div`
  height: calc(100% - ${headerHeight});
  overflow: auto;
  background: #f7f8ff;
`;

const CardShot = styled.div`
  position: relative;
  /* 锁定设计稿宽度：页面再宽也不撑开，布局不会跟着下移 */
  width: 375px;
  max-width: 100%;
  aspect-ratio: 375 / 523;
  margin: 0 auto;
  background: url(${ecodeBg}) center top / 100% 100% no-repeat;
  box-sizing: border-box;
`;

const AvatarWrap = styled.div`
  position: absolute;
  left: 50%;
  top: ${(96 / 523) * 100}%;
  transform: translateX(-50%);
  width: ${(64 / 375) * 100}%;
  aspect-ratio: 1;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  z-index: 2;
  & > div {
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
  }
`;

const WhiteCard = styled.div`
  position: absolute;
  left: ${(50 / 375) * 100}%;
  right: ${(49 / 375) * 100}%;
  top: ${(130 / 523) * 100}%;
  z-index: 1;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 16px;
  padding: 34px 12px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
`;

const Desc = styled.div`
  margin-top: 16px;
  color: #aaa;
  font-size: 14px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  max-width: 420px;
  margin: 20px auto 8px;
  padding: 0 24px;
`;

const Action = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.primary};
`;

const ActionIcon = styled.span<{ $bg: string }>`
  width: 22px;
  height: 20px;
  border-radius: 2px;
  background: ${({ $bg }) => $bg};
  color: #fff;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const LogoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 32px;
`;

const Logo = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 12px;
`;

const LogoText = styled.div`
  margin-top: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #999;
`;

const Extra = styled.div`
  max-width: 420px;
  margin: 0 auto 24px;
  padding: 0 16px;
`;

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const Receive = () => {
  const {
    userStore: { currentUser },
  } = useStores();
  const shotRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const qrcode = currentUser.receiveQrcode;
  const name = currentUser.name || currentUser.username || "";

  const buildCardBlob = async () => {
    const qrCanvas = document.getElementById(QRCODE_ID) as HTMLCanvasElement | null;
    if (!qrCanvas || !qrcode) throw new Error("收款码未生成");

    const width = 375;
    const height = 523;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("无法导出图片");

    try {
      const bg = await loadImage(ecodeBg);
      ctx.drawImage(bg, 0, 0, width, height);
    } catch {
      ctx.fillStyle = "#e8f4ff";
      ctx.fillRect(0, 0, width, height);
    }

    // white card（对齐 App：约 marginTop 130/375）
    const cardTop = 130;
    const cardLeft = 50;
    const cardWidth = width - 99;
    const cardHeight = 300;
    ctx.fillStyle = "rgba(255,255,255,0.96)";
    roundRect(ctx, cardLeft, cardTop, cardWidth, cardHeight, 16);
    ctx.fill();

    // title
    ctx.fillStyle = "#222";
    ctx.font = "bold 16px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`${name}专属收款码`, width / 2, cardTop + 48);

    // qr
    const qrSize = 168;
    const qrX = (width - qrSize) / 2;
    const qrY = cardTop + 64;
    ctx.fillStyle = "#fff";
    ctx.fillRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12);
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    // description
    ctx.fillStyle = "#aaaaaa";
    ctx.font = "14px sans-serif";
    ctx.fillText("扫描二维码 转账给我", width / 2, qrY + qrSize + 28);

    // avatar circle on top（对齐 App：约 top 96/375）
    const avatarSize = 64;
    const ax = width / 2;
    const ay = 96;
    ctx.save();
    ctx.beginPath();
    ctx.arc(ax, ay, avatarSize / 2 + 2, 0, Math.PI * 2);
    ctx.fillStyle = "#fff";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(ax, ay, avatarSize / 2, 0, Math.PI * 2);
    ctx.clip();
    try {
      const avatarUrl = currentUser.avatar
        ? staticUrl(currentUser.avatar)
        : "";
      if (avatarUrl) {
        const av = await loadImage(avatarUrl);
        ctx.drawImage(
          av,
          ax - avatarSize / 2,
          ay - avatarSize / 2,
          avatarSize,
          avatarSize
        );
      } else {
        ctx.fillStyle = "#1c9cea";
        ctx.fillRect(
          ax - avatarSize / 2,
          ay - avatarSize / 2,
          avatarSize,
          avatarSize
        );
        ctx.fillStyle = "#fff";
        ctx.font = "bold 28px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText((name || "?").slice(0, 1), ax, ay);
      }
    } catch {
      ctx.fillStyle = "#1c9cea";
      ctx.fillRect(
        ax - avatarSize / 2,
        ay - avatarSize / 2,
        avatarSize,
        avatarSize
      );
    }
    ctx.restore();

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("导出失败"));
      }, "image/png");
    });
  };

  const saveQrcode = async () => {
    if (busy) return;
    try {
      setBusy(true);
      const blob = await buildCardBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `收款码-${name || "magpie"}.png`;
      a.click();
      URL.revokeObjectURL(url);
      ToastSuccess("已保存收款码");
    } catch (e: any) {
      ToastError(e?.message || "保存失败");
    } finally {
      setBusy(false);
    }
  };

  const shareQrcode = async () => {
    if (busy) return;
    try {
      setBusy(true);
      const blob = await buildCardBlob();
      const file = new File([blob], `收款码-${name || "magpie"}.png`, {
        type: "image/png",
      });
      if (navigator.share && (navigator as any).canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${name}专属收款码`,
          text: "扫描二维码 转账给我",
        });
        ToastSuccess("已分享");
        return;
      }
      // PC 兜底：复制收款链接
      copyTextToClipboard(qrcode);
      ToastSuccess("已复制收款链接，可粘贴分享");
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      try {
        copyTextToClipboard(qrcode);
        ToastSuccess("已复制收款链接，可粘贴分享");
      } catch {
        ToastError(e?.message || "分享失败");
      }
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    if (!qrcode) return ToastError("收款码未生成");
    copyTextToClipboard(qrcode);
    ToastSuccess("已复制收款链接");
  };

  return (
    <PageWrap>
      <PageHeader title="收款" />
      <Body>
        <CardShot ref={shotRef}>
          <AvatarWrap>
            <Avatar avatar={currentUser.avatar} name={name} />
          </AvatarWrap>
          <WhiteCard>
            <Title>{name}专属收款码</Title>
            {qrcode ? (
              <QRCodeCanvas
                id={QRCODE_ID}
                value={qrcode}
                size={168}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                includeMargin={false}
              />
            ) : (
              <div style={{ padding: 40, color: "#999" }}>收款码生成中...</div>
            )}
            <Desc>扫描二维码 转账给我</Desc>
          </WhiteCard>
        </CardShot>

        <Actions>
          <Action type="button" onClick={saveQrcode} disabled={busy}>
            <ActionIcon $bg="#598BFC">↓</ActionIcon>
            保存二维码
          </Action>
          <Action
            type="button"
            onClick={shareQrcode}
            disabled={busy}
            style={{ color: "#25BC97" }}
          >
            <ActionIcon $bg="#25BC97">↗</ActionIcon>
            分享二维码
          </Action>
        </Actions>

        <LogoBox>
          <Logo src={logoImg} alt="喜鹊IM" />
          <LogoText>喜鹊IM</LogoText>
        </LogoBox>

        <Extra>
          <Button block fill="outline" onClick={copyLink}>
            复制收款链接
          </Button>
        </Extra>
      </Body>
    </PageWrap>
  );
};

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

export default observer(Receive);
